import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import dayjs from 'dayjs';
import { InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { formatMinutesToTime } from '@/helpers';
import { cn } from '@/lib/utils';
import { OpsiumUserShortResponse, usePermissions, useTheme } from '@/modules/common';
import { mapRateSourceFromDTO } from '@/modules/common/mappers/rate-source.mapper';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { RateSource } from '@/modules/common/types/rate-source';
import { AssignmentFormValues } from '@/modules/planner/components/modals/assignment-modal/assignment-modal';
import { Themes } from '@/types/theme';
import { normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { DollarSignOffIcon } from '../../../../assets/svgs/dollar-sign-off-icon';
import { ASSIGNMENT_TEXT_CLASSES } from '../../constants/assignment-text';
import { MIN_BAR_WIDTH_FOR_TEXT_REM, PLANNER_TOOLTIP_HALF_WIDTH_PX } from '../../constants/sizes';
import { useAssignmentActions } from '../../contexts/assignment-actions-context';
import { calculateCellWidth, isCompactCellWidth, isDateRangeUnavailable } from '../../helpers';
import { getFragmentDisplayMinutes } from '../../helpers/assignment-timeoff-fragments';
import { isAssignmentFullyOverlappedByTimeoffs } from '../../helpers/assignment-timeoff-overlap';
import {
  findEffectiveActivePeriod,
  findFragmentActivePeriod,
} from '../../helpers/assignment/active-period';
import { getAssignmentTotalBookedMinutes } from '../../helpers/assignment/booked-total';
import { mapAssignmentProjectFromResponse } from '../../mappers/assignment-project.mapper';
import usePlannerStore from '../../store';
import { useTimeoffOverlapStore } from '../../store/timeoff-overlap.store';
import { AssignmentType, ResizeDirection, TimeOffBlockType } from '../../types';
import { AssignmentStatus } from '../../types/assignment-status';
import { PlannerUserStateChangeResponse } from '../../types/dto';
import { AssignmentTooltipContent } from '../tooltip/assignment-tooltip-content';
import { usePlannerTooltip } from '../tooltip/planner-tooltip-provider';
import { AssignmentCutLine } from './assignment-cut-line';
import { AssignmentDuplicatePhantom } from './assignment-duplicate-phantom';
import { AssignmentPhantom } from './assignment-phantom';
import { AssignmentResizeHandles } from './assignment-resize-handles';
import {
  calculateAssignmentOpacity,
  getAssignmentBackgroundColor,
  getAssignmentTextColor,
  getPhantomBackgroundColor,
} from './helpers/assignment-styles';
import { hexToRGBA, isFragmentResizeDisabled } from './helpers/helpers';
import { useAssignmentDragResize, useCutMode, useDuplicateMode } from './hooks';
import { ScrollableText } from './text/scrollable-text';
import { useStickyLabelActive } from './text/sticky-labels-context';

const EMPTY_TIMEOFFS: TimeOffBlockType[] = [];

interface AssignmentProps extends AssignmentType {
  style?: React.CSSProperties;
  user?: OpsiumUserShortResponse;
  stateChanges?: PlannerUserStateChangeResponse[];
  userStartDate?: dayjs.Dayjs;
}

const ASSIGNMENT_TITLE_ANIMATION_DELAY_MS = 300;

export const Assignment = memo(
  ({
    startDate,
    endDate,
    color,
    title,
    hoursPerDay,
    style,
    status,
    disabled,
    originalAssignment,
    activePeriods,
    hasRateChanges,
    user,
    isFilteredOut: isFilteredOutProp,
    belongsToTheGroup,
    bookedMinutes,
    adjustedMinutesByDate,
    stateChanges,
    userStartDate,
    plannerProject,
  }: AssignmentProps) => {
    // Destructure commonly used fields from originalAssignment
    const {
      id,
      note,
      isBillable,
      isBackgroundWork,
      project,
      rateSource,
      doSkipWeekdays,
      customRate,
      rateCurrency,
      syncToken,
    } = originalAssignment;

    const hasStickyLabelFromContext = useStickyLabelActive(id, startDate);

    // The tooltip consumes the domain model — never the raw Response DTO
    const projectDomain = useMemo(
      () => mapAssignmentProjectFromResponse(project, plannerProject),
      [project, plannerProject]
    );

    const { hasPermission } = usePermissions();
    const isEditAssignmentEnabled = hasPermission(OpsiumPolicy.AssignmentsEdit);
    const isViewAssignmentEnabled = hasPermission(OpsiumPolicy.AssignmentsViewAll);
    const isDeleteAssignmentEnabled = hasPermission(OpsiumPolicy.AssignmentsDelete);
    const isCreateAssignmentEnabled = hasPermission(OpsiumPolicy.AssignmentsCreate);

    const { resolvedTheme } = useTheme();
    const isActuallyDark = resolvedTheme === Themes.DARK;

    const { interactiveTooltip } = usePlannerTooltip();
    const {
      show: showTooltip,
      hide: hideTooltipWithDelay,
      hideImmediate: hideTooltipImmediate,
      unpin: unpinTooltip,
      isVisible: isTooltipVisible,
    } = interactiveTooltip;

    const isSplitFragment = doSkipWeekdays || !!adjustedMinutesByDate;

    const isResizeLeftDisabled = useMemo(() => {
      if (!isSplitFragment || !isEditAssignmentEnabled) return false;
      return isFragmentResizeDisabled(
        startDate,
        originalAssignment.startDate,
        ResizeDirection.Forward
      );
    }, [isSplitFragment, startDate, originalAssignment.startDate, isEditAssignmentEnabled]);

    const isResizeRightDisabled = useMemo(() => {
      if (!isSplitFragment || !isEditAssignmentEnabled) return false;
      return isFragmentResizeDisabled(
        endDate,
        originalAssignment.endDate,
        ResizeDirection.Backward
      );
    }, [isSplitFragment, endDate, originalAssignment.endDate, isEditAssignmentEnabled]);

    const isFilteredOut = isFilteredOutProp || !belongsToTheGroup;

    const fragmentPeriod = useMemo(
      () => findFragmentActivePeriod(activePeriods, startDate, endDate),
      [activePeriods, startDate, endDate]
    );

    const { t } = useTranslation('authenticated.planner');
    const tooltipOffsetRef = useRef<number>(0);

    // Get assignment actions from context
    const actions = useAssignmentActions();
    const openTimeoffOverlap = useTimeoffOverlapStore(s => s.open);

    // Combined store subscriptions for better performance
    const {
      interactingAssignmentId,
      isDragResizeInteracting,
      activeCutId,
      activeCutFragmentStart,
      activeDuplicateId,
      hoveredAssignmentId,
      zoomLevel,
      activeDragResizeId,
      setHoveredAssignmentId,
      clearHoveredAssignmentIdIf,
      setShowUnavailableWarning,
      isScrolling,
      showFilteredOutAssignments,
      userTimeOffs,
    } = usePlannerStore(
      useShallow(state => ({
        interactingAssignmentId: state.interactingAssignmentId,
        isDragResizeInteracting: state.isDragResizeInteracting,
        activeCutId: state.activeCutId,
        activeCutFragmentStart: state.activeCutFragmentStart,
        activeDuplicateId: state.activeDuplicateId,
        hoveredAssignmentId: state.hoveredAssignmentId,
        zoomLevel: state.zoomLevel,
        activeDragResizeId: state.activeDragResizeId,
        setHoveredAssignmentId: state.setHoveredAssignmentId,
        clearHoveredAssignmentIdIf: state.clearHoveredAssignmentIdIf,
        setShowUnavailableWarning: state.setShowUnavailableWarning,
        isScrolling: state.isScrolling,
        showFilteredOutAssignments: state.plannerPreferences.showFilteredOutAssignments ?? true,
        userTimeOffs: user?.id
          ? (state.timeOffs.get(String(user.id)) ?? EMPTY_TIMEOFFS)
          : EMPTY_TIMEOFFS,
      }))
    );

    // Sticky labels are hidden (CSS invisible) when any phantom is active (drag/resize,
    // duplicate, create-by-drag). Override to false so ScrollableText becomes visible
    // in the same render frame — both read from the same store values, preventing blink.
    const hasActivePhantom = isDragResizeInteracting || activeDuplicateId !== null;
    const hasStickyLabel = hasStickyLabelFromContext && !hasActivePhantom;

    // DRAG RESIZE
    const {
      phantomRef,
      isDragging,
      isResizing,
      isOptimisticMode,
      showPhantom,
      startOffset,
      duration,
      handleDragStart,
      handleMouseDown,
      resetPosition,
      shouldBlockClick,
    } = useAssignmentDragResize({
      assignmentId: id,
      startDate,
      endDate,
      originalStartDate: dayjs(originalAssignment.startDate),
      originalEndDate: dayjs(originalAssignment.endDate),
      // Block drag/resize entirely without edit permission (OPS-1360). The hook
      // early-returns from its mousedown handlers when disabled.
      disabled: disabled || isScrolling || !isEditAssignmentEnabled,
      onUpdate: (newStart, newEnd) => {
        // Check if new dates overlap unavailable periods (not joined / archived)
        const isUnavailable = isDateRangeUnavailable(stateChanges, userStartDate, newStart, newEnd);
        if (isUnavailable) {
          setShowUnavailableWarning(true);
          resetPosition();
          return;
        }

        // Check if assignment falls fully under approved full-day timeoffs
        const isFullyOverlapped = isAssignmentFullyOverlappedByTimeoffs({
          startDate: newStart,
          endDate: newEnd,
          doSkipWeekdays: doSkipWeekdays ?? false,
          timeOffs: userTimeOffs,
        });
        if (isFullyOverlapped) {
          openTimeoffOverlap({
            assignmentId: id,
            userName: user?.name,
            projectName: project?.name,
          });
          resetPosition();
          return;
        }

        actions.handleValidateAndUpdate({
          assignmentId: id,
          userId: user?.id || 0,
          projectId: project?.id || 0,
          startDate: normalizeToStartOfDayUTC(newStart.toDate()).toISOString(),
          endDate: normalizeToStartOfDayUTC(newEnd.toDate()).toISOString(),
          syncToken: syncToken || '',
          resetPosition,
          ui: {
            userName: user?.name,
            projectName: project?.name,
          },
        });
      },
    });

    // Map current assignment data to modal format
    // Use original assignment dates for editing (not fragment dates)
    const getInitialModalData = (): Partial<
      AssignmentFormValues & {
        scheduledWorkInMinutes: number;
        projectLabel?: string;
        projectColorHex?: string;
        userName?: string;
        userProfileImageUrl?: string;
        syncToken: string;
        projectBoundRateSource?: RateSource | null;
        bookedMinutes?: number;
      }
    > => {
      const effectivePeriod = findEffectiveActivePeriod(
        activePeriods,
        originalAssignment.startDate,
        originalAssignment.endDate
      );

      const periodRate = effectivePeriod?.rate;
      const rateSourceFromPeriod = periodRate
        ? periodRate.assignmentRateSource
        : mapRateSourceFromDTO(originalAssignment.rateSource);
      const rateFromPeriod = periodRate
        ? String(periodRate.rate)
        : customRate != null
          ? String(customRate)
          : '';
      const currencyFromPeriod = periodRate ? periodRate.currency.code : rateCurrency?.code || '';

      return {
        id,
        userName: user?.name || '',
        userProfileImageUrl: user?.profileImageUrl ?? undefined,
        userId: user?.id?.toString(),
        projectId: project?.id?.toString() || '',
        projectLabel: project?.name || undefined,
        projectColorHex: project?.colorHex ?? undefined,
        scheduledWorkInMinutes: hoursPerDay ? hoursPerDay * 60 : 0,
        dateRange: {
          from: new Date(originalAssignment.startDate),
          to: new Date(originalAssignment.endDate),
        },
        status: status,
        probability: originalAssignment.probability,
        isBillable: isBillable ?? true,
        doSkipWeekdays: doSkipWeekdays ?? true,
        isBackgroundWork: isBackgroundWork ?? false,
        rateSource: rateSourceFromPeriod,
        rate: rateFromPeriod,
        currency: currencyFromPeriod,
        note: note || '',
        syncToken,
        projectBoundRateSource: periodRate?.projectBoundRateSource ?? null,
        bookedMinutes,
      };
    };

    const handleEditClick = () => {
      hideTooltipImmediate();
      actions.handleEditAssignment(getInitialModalData());
    };

    // Handle click on assignment to open the modal (only if not dragging/resizing).
    // View-only users (AssignmentsViewAll, no AssignmentsEdit) open it read-only
    // — the modal derives its own read-only state from permissions (OPS-1569).
    const handleAssignmentClick = () => {
      if ((isEditAssignmentEnabled || isViewAssignmentEnabled) && !shouldBlockClick.current) {
        handleEditClick();
      }
    };

    // Calculate and store tooltip align offset based on mouse position
    const handleTooltipOffset = (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX;
      // With align="start", offset is from the left edge of the assignment
      tooltipOffsetRef.current = mouseX - rect.left - PLANNER_TOOLTIP_HALF_WIDTH_PX;
    };

    // Handler for cutting assignment
    const handleCut = (cutDate: dayjs.Dayjs) => {
      actions.handleCutAssignment(
        id,
        normalizeToStartOfDayUTC(cutDate.toDate()).toISOString(),
        syncToken,
        {
          userName: user?.name,
          projectName: project?.name,
        }
      );
    };

    // Handler for opening delete confirmation modal
    const handleDeleteClick = () => {
      hideTooltipImmediate();
      actions.handleOpenDeleteConfirmation(id, {
        userName: user?.name,
        projectName: project?.name,
      });
    };

    // Hide tooltip when entering cut mode so it doesn't block mouse events on the assignment
    const handleToggleCutting = () => {
      if (!isCutting) {
        hideTooltipImmediate();
      }
      toggleCutting(startDate, endDate);
    };

    // Hide tooltip and enter duplicate mode
    const handleToggleDuplicating = () => {
      if (!isDuplicating) {
        hideTooltipImmediate();
      }
      toggleDuplicating();
    };

    // CUT MODE
    const {
      isCutting,
      cutOffset,
      cutStartOffset: cutModeStartOffset,
      cutDuration: cutModeDuration,
      disableCutting,
      toggleCutting,
      handleClickForCut,
      handleMouseMoveForCut,
      assignmentRef,
    } = useCutMode({
      id,
      originalStartDate: dayjs(originalAssignment.startDate),
      originalEndDate: dayjs(originalAssignment.endDate),
      onCut: handleCut,
    });

    // Handler for duplicating assignment
    const handleDuplicate = (newStartDate: dayjs.Dayjs, newEndDate: dayjs.Dayjs) => {
      // Validate the new dates don't overlap before duplicating
      actions.handleValidateAndDuplicate({
        assignmentId: id,
        startDate: normalizeToStartOfDayUTC(newStartDate.toDate()).toISOString(),
        endDate: normalizeToStartOfDayUTC(newEndDate.toDate()).toISOString(),
        // Pass source assignment data for the optimistic cache update and toasts
        ui: {
          userId: user?.id || 0,
          projectId: originalAssignment.project.id,
          userName: user?.name,
          projectName: project?.name,
          projectColorHex: color,
          minutesPerDay: originalAssignment.minutesPerDay,
          status: originalAssignment.status,
          isBillable: isBillable,
          doSkipWeekdays: doSkipWeekdays,
          note: note || '',
          customRate: customRate ?? undefined,
          label: originalAssignment.label,
          isBackgroundWork: isBackgroundWork,
          probability: originalAssignment.probability,
          rateSource,
        },
      });
    };

    // DUPLICATE MODE
    const {
      isDuplicating,
      duplicateOffset,
      duration: duplicateDuration,
      toggleDuplicating,
      setTimelineRow,
    } = useDuplicateMode({
      id,
      startDate: dayjs(originalAssignment.startDate),
      endDate: dayjs(originalAssignment.endDate),
      onDuplicate: handleDuplicate,
    });

    // The fragment that the user interacted with becomes the active cutter and renders the full assignment.
    // We match by comparing the fragment's start date to the stored activeCutFragmentStart.
    // For non-skip-weekdays assignments there's a single fragment so this is always true when cutting.
    const isActiveCutFragment =
      isCutting &&
      (activeCutFragmentStart
        ? startDate.startOf('day').toISOString() === activeCutFragmentStart
        : !isResizeLeftDisabled);

    // Show only the InfoIcon while the bar is too narrow to fit text. As zoom widens
    // the bar past MIN_BAR_WIDTH_FOR_TEXT_REM, the normal time+title text renders and the
    // text-fit / sticky-label / scroll-on-hover system takes over (same as wide bars).
    // Width-based (not a hardcoded === day count) so it scales with zoom + minimize.
    const isOneDayFragment = duration < MIN_BAR_WIDTH_FOR_TEXT_REM;
    // During cut mode the full assignment is displayed, so never treat it as one-day
    const isOneDayAssignment = isOneDayFragment && !isActiveCutFragment;

    const isDragDisabled = isCutting || isDuplicating;

    // Hide all fragments of the same assignment during drag/resize/cut (except the active one).
    const shouldHideFragment =
      interactingAssignmentId === id &&
      !isDragging &&
      !isResizing &&
      !isOptimisticMode &&
      !isActiveCutFragment;

    const opacity = calculateAssignmentOpacity({
      isResizing: !!isResizing,
      isDragging,
      showPhantom,
      disabled: !!disabled,
      isFilteredOut,
      isCutting: false,
      isDuplicating,
    });

    // Assignment status checks (needed for styling)
    const isDraft = status === AssignmentStatus.Draft;
    const isUnconfirmed = status === AssignmentStatus.Unconfirmed;

    // Border color: uses CSS variable fallback for theming support
    const projectColorBorder = hexToRGBA(color, 1);

    const phantomBackgroundColor = getPhantomBackgroundColor({
      color,
      status,
    });

    const assignmentBackgroundColor = getAssignmentBackgroundColor({
      color,
      status,
      isActuallyDark,
      calculatedOpacity: opacity,
    });

    const textColor = getAssignmentTextColor(status, color);

    // On a day where a partial (e.g. half-day) time-off overlaps the assignment, the
    // backend reduces the effective minutes (adjustedMinutesByDate). This fragment is
    // isolated to that day, so display the reduced hours (e.g. 4h) instead of the
    // assignment's full minutesPerDay (8h). See getFragmentDisplayMinutes / OPS-1469.
    const displayMinutesPerDay = useMemo(
      () =>
        getFragmentDisplayMinutes(
          { fullMinutes: originalAssignment.minutesPerDay, adjustedMinutesByDate },
          startDate,
          endDate
        ),
      [originalAssignment.minutesPerDay, adjustedMinutesByDate, startDate, endDate]
    );
    const displayHoursPerDay = displayMinutesPerDay / 60;

    // From the assignment, not the fragment: fragments are clipped to the viewport
    const totalBookedMinutes = useMemo(
      () => getAssignmentTotalBookedMinutes(bookedMinutes),
      [bookedMinutes]
    );

    const assignmentTitle = t('assignmentLabel', {
      time: formatMinutesToTime(displayMinutesPerDay),
      title,
    });

    // Compact display (smaller icon, tighter phantom padding) is derived from the
    // current cell width, which scales with zoom — replaces the old minimizeMode flag.
    const cellWidth = calculateCellWidth(zoomLevel);
    const isCompact = isCompactCellWidth(cellWidth);
    const oneDayFragmentIconSize = isCompact ? 12 : 16;

    // Guarded clear for the tooltip's deferred onHide. Each fragment is a separate
    // component instance, so this closure differs per fragment even for the same id
    // — the cross-fragment lift is preserved instead via the `hideKey` (= id) passed
    // to showTooltip below, which tells the tooltip not to run the previous onHide
    // when moving between fragments/segments of the SAME assignment.
    const handleAssignmentHide = useCallback(
      () => clearHoveredAssignmentIdIf(id),
      [clearHoveredAssignmentIdIf, id]
    );

    // Show interactive tooltip on mouse enter
    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        // Always set hovered state (needed for cut line visibility and hover styling)
        setHoveredAssignmentId(id);

        // Don't show tooltip during active interactions
        if (activeDuplicateId || activeCutId || activeDragResizeId) return;

        handleTooltipOffset(e);

        showTooltip(
          <AssignmentTooltipContent
            user={user}
            project={projectDomain}
            color={color}
            status={status}
            probability={originalAssignment.probability}
            isBillable={isBillable}
            fragmentRate={fragmentPeriod?.rate}
            fragmentRateChangeDate={fragmentPeriod?.rateChangeDate}
            hasRateChanges={hasRateChanges}
            originalStartDate={originalAssignment.startDate}
            originalEndDate={originalAssignment.endDate}
            hoursPerDay={displayHoursPerDay}
            totalBookedMinutes={totalBookedMinutes}
            note={note}
            isBackgroundWork={isBackgroundWork}
            isCutting={isCutting}
            isDuplicating={isDuplicating}
            disableCutting={disableCutting}
            disabled={!!disabled}
            onEditClick={handleEditClick}
            onToggleCutting={handleToggleCutting}
            onDeleteClick={handleDeleteClick}
            onToggleDuplicating={handleToggleDuplicating}
            isDeleteAssignmentEnabled={isDeleteAssignmentEnabled}
            isCreateAssignmentEnabled={isCreateAssignmentEnabled}
            isEditAssignmentEnabled={isEditAssignmentEnabled}
          />,
          e.currentTarget,
          tooltipOffsetRef.current,
          handleAssignmentHide,
          // hideKey = assignment id: moving between fragments of the SAME assignment
          // keeps the lift; moving to a different assignment still clears it.
          id
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        id,
        activeDuplicateId,
        activeCutId,
        activeDragResizeId,
        user,
        project,
        color,
        status,
        isBillable,
        fragmentPeriod,
        hasRateChanges,
        originalAssignment.startDate,
        originalAssignment.endDate,
        hoursPerDay,
        totalBookedMinutes,
        note,
        isCutting,
        isDuplicating,
        disableCutting,
        disabled,
        toggleCutting,
        toggleDuplicating,
        showTooltip,
        setHoveredAssignmentId,
        handleAssignmentHide,
      ]
    );

    // Hide interactive tooltip on mouse leave (with delay for bridge)
    const handleMouseLeave = useCallback(() => {
      // During cut mode, keep hovered state so the assignment stays visually active
      if (isCutting) {
        return;
      }
      // Only clear hover state immediately if tooltip hasn't shown yet (fast cursor movement case)
      // If tooltip is visible, keep the hover state so assignment stays "up" while user interacts with tooltip
      if (!isTooltipVisible) {
        setHoveredAssignmentId(null);
      }
      // Hide tooltip with delay (for bridge hover-through)
      hideTooltipWithDelay();
    }, [hideTooltipWithDelay, isCutting, isTooltipVisible, setHoveredAssignmentId]);

    // Hide interactive tooltip when drag/resize or duplicate mode activates
    useEffect(() => {
      if (activeDragResizeId || activeDuplicateId) {
        hideTooltipImmediate();
      }
    }, [activeDragResizeId, activeDuplicateId, hideTooltipImmediate]);

    // When cut mode ends (Escape, click outside, or cut performed), clean up tooltip and hover state.
    // During cut mode handleMouseLeave is suppressed, so hoveredAssignmentId is never cleared —
    // reset it here to remove the -translate-y-1 lift effect.
    useEffect(() => {
      if (!isCutting) {
        unpinTooltip();
        setHoveredAssignmentId(null);
      }
    }, [isCutting, unpinTooltip, setHoveredAssignmentId]);

    // Hide other fragments when one fragment of the same assignment is being dragged/resized
    if (shouldHideFragment) {
      return null;
    }

    // Hide filtered-out assignments entirely when the preference is disabled
    if (isFilteredOut && !showFilteredOutAssignments) {
      return null;
    }

    return (
      <div
        ref={el => {
          if (el && isDuplicating) {
            setTimelineRow(el.parentElement as HTMLElement);
          }
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          ref={assignmentRef}
          data-assignment-id={id}
          className={cn(
            'group relative p-2 h-7 select-none rounded-[100px] assignment flex items-center transition-transform duration-150',
            {
              'cursor-default': !isEditAssignmentEnabled || disabled || isDragDisabled,
              'cursor-move': isEditAssignmentEnabled && !disabled && !isDragDisabled,
              'overflow-hidden':
                !isDragging &&
                !isResizing &&
                !isDuplicating &&
                !isActiveCutFragment &&
                !isOneDayAssignment,
              '-translate-y-1':
                hoveredAssignmentId === id &&
                !isDragging &&
                !isResizing &&
                !isCutting &&
                !isDuplicating &&
                !isDragResizeInteracting &&
                activeCutId === null &&
                activeDuplicateId === null,
              'pointer-events-none':
                activeDragResizeId || activeDuplicateId || (activeCutId && activeCutId !== id),
            }
          )}
          onMouseDown={isDragDisabled ? undefined : handleDragStart}
          onMouseMove={handleMouseMoveForCut}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            // Check if we're in cut/duplicate mode before calling click handler
            const wasInSpecialMode = isCutting || isDuplicating;
            handleClickForCut();
            // Only handle assignment click if not in special mode
            if (!wasInSpecialMode) {
              handleAssignmentClick();
            }
          }}
          style={{
            left: `${isActiveCutFragment ? cutModeStartOffset : startOffset}rem`,
            width: `${isActiveCutFragment ? cutModeDuration : duration}rem`,
            position: 'absolute',
            border: isUnconfirmed ? `1px dashed ${projectColorBorder}` : undefined,
            // z-1 ties the APPROVED time-off block's z-[1] and wins by DOM source
            // order (assignments render after blocks), so on a partial approved day
            // (half/quarter/custom) the reduced+repositioned bar stays hoverable
            // instead of being covered by the block. The block still catches hover
            // where no bar sits. PENDING (z-0) is unaffected; cut fragment keeps 50.
            zIndex: isActiveCutFragment ? 50 : 1,
            backgroundColor: assignmentBackgroundColor,
            backgroundImage: isDraft
              ? `repeating-linear-gradient(
                                  -30deg,
                                  ${assignmentBackgroundColor},
                                  ${assignmentBackgroundColor} 3px,
                                  transparent 3px,
                                  transparent 6px
                                )`
              : undefined,
            backgroundSize: `${isActiveCutFragment ? cutModeDuration : duration}rem 28px`,
            ...style,
          }}
        >
          {/* One-day: icon rendered directly (no ScrollableText) to avoid overflow clipping */}
          {isOneDayAssignment && (
            <div
              className={cn(
                ASSIGNMENT_TEXT_CLASSES,
                'absolute left-1/2 top-1/2 w-fit -translate-x-1/2 -translate-y-1/2 pl-1'
              )}
              style={{ color: textColor, opacity }}
            >
              <InfoIcon size={oneDayFragmentIconSize} className="-ml-1" />
            </div>
          )}
          {/* Multi-day: negative margin extends hover area to full bar, padding keeps text position */}
          {!isOneDayAssignment && (
            <ScrollableText
              className={cn(ASSIGNMENT_TEXT_CLASSES, '-m-2 w-[calc(100%+16px)] px-[12px] py-2', {
                invisible: hasStickyLabel,
              })}
              style={{
                color: textColor,
                opacity,
              }}
              disabled={isDragging || !!isResizing || isCutting}
              animationDelayMs={ASSIGNMENT_TITLE_ANIMATION_DELAY_MS}
            >
              <span className="flex min-w-0 items-center gap-0.5">
                {!isBillable && <DollarSignOffIcon size={14} className="flex-shrink-0" />}
                <span className="truncate">{assignmentTitle}</span>
              </span>
            </ScrollableText>
          )}

          {/* phantom element */}
          {showPhantom && (
            <AssignmentPhantom
              ref={phantomRef}
              duration={duration}
              assignmentTitle={assignmentTitle}
              textColor={textColor}
              phantomBackgroundColor={phantomBackgroundColor}
              isResizing={isResizing || false}
              isDragging={isDragging}
              isDraft={isDraft}
              isUnconfirmed={isUnconfirmed}
              projectColorBorder={projectColorBorder}
              isOneDayAssignment={isOneDayAssignment}
            />
          )}

          {/* cut line */}
          {isActiveCutFragment && cutOffset !== null && (
            <AssignmentCutLine cutOffset={cutOffset} assignmentRef={assignmentRef} />
          )}

          {/* duplicate phantom */}
          {isDuplicating && duplicateOffset !== null && (
            <AssignmentDuplicatePhantom
              duplicateOffset={duplicateOffset}
              startOffset={startOffset}
              duplicateDuration={duplicateDuration}
              phantomBackgroundColor={phantomBackgroundColor}
              projectColorBorder={projectColorBorder}
              isOneDayAssignment={isOneDayAssignment}
              isCompact={isCompact}
              assignmentTitle={assignmentTitle}
              oneDayFragmentIconSize={oneDayFragmentIconSize}
            />
          )}

          {isEditAssignmentEnabled && !disabled && !isCutting && !isDuplicating && (
            <AssignmentResizeHandles
              isResizeLeftDisabled={isResizeLeftDisabled}
              isResizeRightDisabled={isResizeRightDisabled}
              isResizing={isResizing || false}
              isDragging={isDragging}
              textColor={textColor}
              opacity={opacity}
              onMouseDownLeft={e => {
                handleMouseDown('left', e);
                e.stopPropagation();
              }}
              onMouseDownRight={e => {
                handleMouseDown('right', e);
                e.stopPropagation();
              }}
            />
          )}
        </div>
      </div>
    );
  }
);
